// View-only PDF reader — full-screen modal.
//
// The file is streamed from the authenticated endpoint and rendered
// page-by-page onto <canvas> via pdf.js, so there is no browser PDF toolbar,
// no download button and no print control. Right-click and text selection are
// disabled. This is deterrence, not DRM — anything renderable can ultimately
// be screen-captured — but there is no download affordance anywhere.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { X, ZoomIn, ZoomOut, FileText } from "lucide-react";
import { API_URL } from "../../api/api";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 2];
// .so-viewer-scroll's vertical padding — kept out of the page-fit maths.
const SCROLL_PADDING = 64;

export default function PdfViewerModal({ doc, onClose, onViewed }) {
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState(null);
  const [zoomIdx, setZoomIdx] = useState(1); // 100%
  const pagesRef = useRef(null);
  const onViewedRef = useRef(onViewed);
  onViewedRef.current = onViewed;

  // Load the document once.
  useEffect(() => {
    let cancelled = false;
    let loadedPdf = null;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/signoff/documents/${doc.id}/file`,
          { ...auth(), responseType: "arraybuffer" },
        );
        const task = pdfjsLib.getDocument({ data: res.data });
        loadedPdf = await task.promise;
        if (cancelled) return;
        setPdf(loadedPdf);
        onViewedRef.current?.(doc.id);
      } catch (e) {
        if (!cancelled)
          setError("Could not open this document. Please try again.");
      }
    })();
    return () => {
      cancelled = true;
      loadedPdf?.destroy?.();
    };
  }, [doc.id]);

  // Render every page whenever the pdf or zoom changes.
  const renderPages = useCallback(async () => {
    const host = pagesRef.current;
    if (!pdf || !host) return;
    host.innerHTML = "";
    const scroller = host.parentElement;
    // Fit a whole page in the window at 100%: an A4 sheet is far taller than
    // it is wide, so sizing on width alone leaves the reader scrolling to see
    // a single page. Zoom still scales past this.
    // clientWidth is 0 before layout — fall back rather than compute a
    // negative page width.
    const available = host.clientWidth ? host.clientWidth - 8 : 900;
    const maxWidth = Math.max(320, Math.min(available, 900));
    const maxHeight = Math.max(
      360,
      (scroller?.clientHeight || window.innerHeight) - SCROLL_PADDING,
    );
    const dpr = window.devicePixelRatio || 1;
    const zoom = ZOOM_STEPS[zoomIdx];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const base = page.getViewport({ scale: 1 });
      const fit = Math.min(maxWidth / base.width, maxHeight / base.height);
      const scale = fit * zoom;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      canvas.className = "so-viewer-page";
      host.appendChild(canvas);
      const ctx = canvas.getContext("2d");
      await page.render({
        canvasContext: ctx,
        viewport,
        transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
      }).promise;
    }
  }, [pdf, zoomIdx]);

  useEffect(() => {
    renderPages();
  }, [renderPages]);

  // Re-fit the pages when the window changes size.
  useEffect(() => {
    let timer = null;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(renderPages, 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
  }, [renderPages]);

  // Esc closes; block Ctrl/Cmd+S and Ctrl/Cmd+P while open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if ((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "p")) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Rendered into <body>: callers mount this inside cards that use
  // backdrop-filter, and a filtered ancestor becomes the containing block for
  // `position: fixed`, which would trap this "full-screen" reader inside the
  // card's box.
  return createPortal(
    <div
      className="so-viewer-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={doc.title}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="so-viewer-bar">
        <span className="so-viewer-doctitle">
          <FileText size={16} />
          <span>{doc.title}</span>
          {pdf && <em>{pdf.numPages} page{pdf.numPages === 1 ? "" : "s"}</em>}
        </span>
        <div className="so-viewer-tools">
          <button
            type="button"
            aria-label="Zoom out"
            disabled={zoomIdx === 0}
            onClick={() => setZoomIdx((z) => Math.max(0, z - 1))}
          >
            <ZoomOut size={16} />
          </button>
          <span className="so-viewer-zoom">{Math.round(ZOOM_STEPS[zoomIdx] * 100)}%</span>
          <button
            type="button"
            aria-label="Zoom in"
            disabled={zoomIdx === ZOOM_STEPS.length - 1}
            onClick={() => setZoomIdx((z) => Math.min(ZOOM_STEPS.length - 1, z + 1))}
          >
            <ZoomIn size={16} />
          </button>
          <button type="button" className="so-viewer-close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="so-viewer-scroll">
        {error ? (
          <div className="so-viewer-error">{error}</div>
        ) : !pdf ? (
          <div className="so-viewer-loading">Opening document…</div>
        ) : null}
        <div ref={pagesRef} className="so-viewer-pages" />
      </div>
    </div>,
    document.body,
  );
}
